import React, { type ReactNode } from 'react';

import { AssetDetails } from '@pkg/components';
import { nativeCurrencyFromChain } from '@pkg/hooks/nft/utilities';
import { type BaseCurrencyType, type MarketplaceAsset } from '@pkg/types';

import { useExchangeRatesStore } from '@app/store/zustand';

import {
    Actions,
    EditablePrice,
    type EditablePriceProps,
    type LocalCurrencySelectorProps,
    NonEditablePrice,
    StatsList,
} from './parts';

import { AssetDetailsWrapper, ChildrenWrapper, ErrorMessage, Row, StyledAssetCard } from './AssetCard.style';

export interface AssetCardProps {
    asset: MarketplaceAsset;
    children?: ReactNode;
    localCurrency?: BaseCurrencyType;
    localPrice?: EditablePriceProps['localPrice'];
    nativePrice?: EditablePriceProps['nativePrice'];
    appraisal?: EditablePriceProps['appraisal'];
    error?: string;
    onPriceBlur?: EditablePriceProps['onPriceBlur'];
    onPriceChange?: EditablePriceProps['onPriceChange'];
    onLocalCurrencyChange?: LocalCurrencySelectorProps['onSelect'];
    // TODO: Restore `expand` once we support multiple items in a cart.
    // expand?: boolean;
}

export function AssetCard({
    asset,
    children,
    localCurrency = 'usd',
    localPrice,
    nativePrice,
    appraisal,
    error,
    onPriceBlur,
    onPriceChange,
    onLocalCurrencyChange,
}: AssetCardProps) {
    // Pass `$expandable` to `<Row />` once we are ready.
    const childrenMarkup = children ? (
        <Row>
            <ChildrenWrapper>{children}</ChildrenWrapper>
        </Row>
    ) : null;

    const nativeCurrency = nativeCurrencyFromChain(asset.collection.chain);
    const convertCurrency = useExchangeRatesStore((state) => state.convertCurrency);

    // TODO: Consider simplifying by offering separate components:
    // <AssetCard /> vs <AssetCardEditable />
    const editable = Boolean(onPriceBlur || onPriceChange || onLocalCurrencyChange);

    const priceMarkup = editable ? (
        <EditablePrice
            localCurrency={localCurrency}
            nativeCurrency={nativeCurrency}
            localPrice={localPrice}
            nativePrice={nativePrice}
            appraisal={appraisal}
            error={Boolean(error)}
            onPriceBlur={onPriceBlur}
            onPriceChange={onPriceChange}
            onLocalCurrencyChange={onLocalCurrencyChange}
        />
    ) : (
        <NonEditablePrice
            usdPrice={convertCurrency(nativeCurrency, 'usd', parseFloat(String(nativePrice ?? 0)))}
            nativePrice={nativePrice}
            nativeCurrency={nativeCurrency}
        />
    );

    const errorMarkup = error ? <ErrorMessage>{error}</ErrorMessage> : null;

    // Pass `$expand={expand}` to <StyledAssetCard /> once we are ready.
    return (
        <StyledAssetCard>
            <Row>
                <AssetDetailsWrapper>
                    <AssetDetails name={asset.name} collectionName={asset.collection.name} imageUrl={asset.imageUrl} />
                </AssetDetailsWrapper>

                {priceMarkup}
            </Row>

            {childrenMarkup}
            {errorMarkup}
        </StyledAssetCard>
    );
}

AssetCard.Actions = Actions;
AssetCard.StatsList = StatsList;
